import request from '@/utils/request'
import { requireStoreId } from '@/utils/business-context'
import { getStaticContentTasks } from '@/api/static-data'

export async function getBusinessKeywords(storeId = 0) {
  const resolvedStoreId = Number(storeId) || await requireStoreId()
  return request.get('/mate/getkeywords', { params: { storeId: resolvedStoreId } })
}

export function getStoreList() {
  return request.get('/business/getstorelist', { params: { type: 0, page: 1, limit: 100 } })
}

export function getStoreInfo(storeId) {
  return request.get('/business/getstoreinfo', {
    params: { id: storeId },
    silent: true
  })
}

export function createStore({ name, cdkey, image = '' }) {
  return request.post('/business/createstore', {
    name: String(name || '').trim(),
    cdkey: String(cdkey || '').trim(),
    image
  })
}

export async function activateStoreCard(storeId, cdkey) {
  const resolvedStoreId = Number(storeId) || await requireStoreId()
  const value = String(cdkey || '').trim()
  if (!value) throw new Error('请输入卡密')
  return request.post('/business/usecdkey', { storeId: resolvedStoreId, cdkey: value })
}

export function getStoreAuthCode(storeId) {
  return request.get('/business/getstoreauthcode', {
    params: { storeId },
    silent: true
  })
}

export function getStoreAuthList(storeId) {
  return request.get('/business/getstoreauthlist', {
    params: { storeId },
    silent: true
  })
}

export function submitStoreAuthCode(authCode) {
  return request.post('/business/submitauthcode', { authCode })
}

export function cancelStoreAuth(storeId, userId) {
  return request.post('/business/cancelauth', { storeId, userId })
}

export function deleteStoreAuth(storeId) {
  return request.post('/business/deleteauth', { storeId })
}

export async function saveBusinessKeywords(keywords) {
  const storeId = await requireStoreId()
  return request.post('/mate/setkeywords', { storeId, keywords })
}

export async function getMediaGroups(name = '') {
  const storeId = await requireStoreId()
  return request.get('/mate/getgrouplistnopage', { params: { storeId, classId: 101, name, page: 1, limit: 100 } })
}

export async function getMediaList({ groupId = 0, name = '', page = 1, limit = 24 } = {}) {
  const storeId = await requireStoreId()
  return request.get('/mate/getmatelist', { params: { storeId, classId: 101, groupId, name, page, limit } })
}

export async function saveMediaGroup({ groupId = 0, groupName }) {
  const storeId = await requireStoreId()
  return request.post('/mate/savegroup', { storeId, classId: 101, groupId, groupName })
}

export function deleteMediaGroup(groupId) {
  return request.post('/mate/deletegroup', { groupId })
}

export function deleteMedia(mateId) {
  return request.post('/mate/deletemate', { mateId })
}

const VIDEO_METADATA_TIMEOUT = 15000
const VIDEO_METADATA_POLL_INTERVAL = 100
const VIDEO_MAX_SIZE = 50 * 1024 * 1024

function readVideoMetadata(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const url = URL.createObjectURL(file)
    const metadataEvents = ['loadedmetadata', 'loadeddata', 'canplay', 'durationchange', 'resize', 'seeked']
    let settled = false
    let seekRequested = false

    const currentMetadata = () => ({
      width: Math.round(Number(video.videoWidth) || 0),
      height: Math.round(Number(video.videoHeight) || 0),
      duration: Number(video.duration)
    })
    const cleanup = () => {
      window.clearTimeout(timeoutId)
      window.clearInterval(pollId)
      metadataEvents.forEach(event => video.removeEventListener(event, inspectMetadata))
      video.removeEventListener('error', handleError)
      video.pause()
      video.removeAttribute('src')
      video.load()
      URL.revokeObjectURL(url)
    }
    const finish = (error, metadata) => {
      if (settled) return
      settled = true
      cleanup()
      if (error) reject(error)
      else resolve(metadata)
    }
    const inspectMetadata = () => {
      const metadata = currentMetadata()
      if (metadata.width > 0 && metadata.height > 0 && Number.isFinite(metadata.duration) && metadata.duration > 0) {
        finish(null, metadata)
        return
      }

      // Some MOV/MP4 files expose duration before their dimensions. Seeking a
      // small amount prompts those browsers to decode the first video frame.
      if (!seekRequested && video.readyState >= 1 && Number.isFinite(metadata.duration) && metadata.duration > 0.02) {
        seekRequested = true
        try {
          video.currentTime = Math.min(0.05, metadata.duration / 2)
        } catch (error) {
          // Event polling below still handles browsers that disallow early seeks.
        }
      }
    }
    const handleError = () => {
      finish(new Error(`无法解析“${file.name}”的视频信息，请确认文件可正常播放或转换为标准 MP4 后重试`))
    }
    const timeoutId = window.setTimeout(() => {
      const metadata = currentMetadata()
      const field = metadata.width <= 0 || metadata.height <= 0 ? '宽高' : '时长'
      finish(new Error(`未能读取“${file.name}”的视频${field}，请转换为标准 MP4（H.264）后重试`))
    }, VIDEO_METADATA_TIMEOUT)
    const pollId = window.setInterval(inspectMetadata, VIDEO_METADATA_POLL_INTERVAL)

    metadataEvents.forEach(event => video.addEventListener(event, inspectMetadata))
    video.addEventListener('error', handleError)
    video.preload = 'auto'
    video.muted = true
    video.playsInline = true
    video.setAttribute('playsinline', '')
    video.src = url
    video.load()
  })
}

function validateVideoFile(file) {
  if (!file || typeof file.size !== 'number') throw new Error('视频文件无效，请重新选择')
  if (!file.size) throw new Error(`${file.name || '所选文件'} 内容为空`)
  if (file.size > VIDEO_MAX_SIZE) throw new Error(`${file.name} 超过 50MB 限制`)

  const fileName = String(file.name || '')
  const fileType = String(file.type || '').toLowerCase()
  const hasSupportedExtension = /\.(mp4|mov)$/i.test(fileName)
  const hasSupportedType = ['video/mp4', 'video/quicktime'].includes(fileType)
  if (!hasSupportedExtension && !hasSupportedType) {
    throw new Error(`${fileName || '所选文件'} 格式不支持，请选择 MP4 或 MOV 视频`)
  }
}

function validateVideoMetadata(file, metadata) {
  if (!Number.isInteger(metadata.width) || metadata.width <= 0 || !Number.isInteger(metadata.height) || metadata.height <= 0) {
    throw new Error(`未能读取“${file.name}”的视频宽高，请转换为标准 MP4（H.264）后重试`)
  }
  if (!Number.isFinite(metadata.duration) || metadata.duration <= 0) {
    throw new Error(`未能读取“${file.name}”的视频时长，请确认文件可正常播放`)
  }
  if (metadata.duration < 2.5 || metadata.duration > 61) {
    throw new Error(`${file.name} 时长需在 3-60 秒之间`)
  }
}

function uploadToStorage(sign, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const formData = new FormData()
    Object.keys(sign.formData || {}).forEach(key => formData.append(key, sign.formData[key]))
    formData.append('file', file)
    xhr.open('POST', sign.host, true)
    xhr.upload.onprogress = event => {
      if (event.lengthComputable && onProgress) onProgress(Math.round(event.loaded / event.total * 100))
    }
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) return reject(new Error(`上传失败（${xhr.status}）`))
      try {
        const payload = JSON.parse(xhr.responseText || '{}')
        if (typeof payload.code !== 'undefined' && Number(payload.code) !== 0) return reject(new Error(payload.msg || '上传失败'))
        resolve(payload.data || payload)
      } catch (error) {
        resolve(null)
      }
    }
    xhr.onerror = () => reject(new Error('上传连接失败'))
    xhr.send(formData)
  })
}

export async function uploadVideoMaterial(file, groupId, onProgress) {
  validateVideoFile(file)
  const storeId = await requireStoreId()
  const metadata = await readVideoMetadata(file)
  validateVideoMetadata(file, metadata)
  const sign = await request.post('/mate/applyuploadvideo', {
    storeId,
    fileName: file.name,
    classId: 101,
    groupId,
    width: metadata.width,
    height: metadata.height,
    duration: metadata.duration
  }, { silent: true })
  return uploadToStorage(sign, file, onProgress)
}

export async function getWorks({ name = '', page = 1, limit = 10 } = {}) {
  const storeId = await requireStoreId()
  return request.get('/muban/getlist', { params: { storeId, name, page, limit } })
}

export async function getEditorTemplateDetail(mubanId) {
  const storeId = await requireStoreId()
  return request.get('/muban/detail2', { params: { storeId, mubanId } })
}

export async function getWorkVideos({ mubanId, clipContainerId = 0, page = 1, limit = 12 } = {}) {
  const storeId = await requireStoreId()
  return request.get('/alone/getcliptaskvideolist', {
    params: {
      mubanId,
      clipContainerId,
      storeId,
      isFabu: 0,
      status: 0,
      scanAccountType: 0,
      autoAccountType: 0,
      method: 1,
      page,
      limit
    }
  })
}

export function getWorkVideoUrl(clipTaskId) {
  return request.get('/clip/geturl', { params: { clipTaskId } })
}

export async function submitWorkMuban(mubanId, { silent = false } = {}) {
  const storeId = await requireStoreId()
  return request.post('/clip/submitmuban', { mubanId, storeId }, { silent })
}

export function deleteWork(mubanId) {
  return request.post('/muban/delete', { mubanId })
}

export function hideMubanClipTasks(mubanId) {
  return request.post('/muban/hidecliptask', { mubanId }, { silent: true })
}

export function getContentTasks() {
  return getStaticContentTasks()
}

export async function uploadCopyTitles(titles, classId = 401) {
  const storeId = await requireStoreId()
  return request.post('/mate/uploadtitle', {
    storeId,
    groupId: 0,
    classId,
    titles
  })
}

export async function getSystemMaterials(classId, { page = 1, limit = 100, groupId = 0 } = {}) {
  const storeId = await requireStoreId()
  return request.get('/mate/getsystemmatelist', {
    params: { storeId, classId, groupId, page, limit }
  })
}

export async function getVoiceGroups(name = '') {
  const storeId = await requireStoreId()
  return request.get('/mate/getgrouplistnopage', {
    params: { storeId, classId: 302, name, page: 1, limit: 100 }
  })
}

export async function getMyVoices({ groupId = 0, page = 1, limit = 20 } = {}) {
  const storeId = await requireStoreId()
  return request.get('/mate/getmatelist', {
    params: { storeId, classId: 302, groupId, page, limit }
  })
}

export async function saveVoiceGroup({ groupId = 0, groupName }) {
  const storeId = await requireStoreId()
  return request.post('/mate/savegroup', { storeId, classId: 302, groupId, groupName })
}

export function deleteVoiceGroup(groupId) {
  return request.post('/mate/deletegroup', { groupId })
}

export function deleteVoice(mateId) {
  return request.post('/mate/deletemate', { mateId })
}

export async function uploadVoiceMaterial(file, { name = '', groupId = 0, onProgress } = {}) {
  if (!file) throw new Error('请选择音频文件')
  if (file.size > 20 * 1024 * 1024) throw new Error(`${file.name} 超过 20MB 限制`)
  const storeId = await requireStoreId()
  const sign = await request.post('/mate/applyuploadvoice', { fileName: file.name, storeId })
  const uploaded = await uploadToStorage(sign, file, onProgress)
  if (!uploaded || !uploaded.ossId) throw new Error('上传结果缺少音频标识')
  return request.post('/mate/savematevoicedata', {
    storeId,
    groupId,
    ossId: uploaded.ossId,
    name: name || file.name.replace(/\.[^.]+$/, '')
  })
}

export function getTransitionList() {
  return request.get('/mate/gettransitionlist')
}

export function getAiTemplateTypes(group = 1) {
  return request.get('/aichat/getaichattemplatetype', { params: { group } })
}

export async function getAiTemplate(type) {
  const storeId = await requireStoreId()
  return request.get('/aichat/getaichattemplate', { params: { storeId, type } })
}

export async function createAiCopy({ AiModel = 0, templateId = 0, question, sessionId = '' }) {
  const storeId = await requireStoreId()
  return request.post('/aichat/all', { storeId, AiModel, templateId, question, sessionId })
}

export async function saveEditorTemplate(payload) {
  const storeId = await requireStoreId()
  return request.post('/muban/save2', { ...payload, storeId })
}
